// Utility script to make a user an admin
// Usage: Import this function and call it with the user's email

import { doc, getDoc, setDoc, collection, getDocs, deleteDoc, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { deleteUser } from 'firebase/auth';
import { auth } from './firebase';

// Make a user an admin
export const makeUserAdmin = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, { role: 'admin' }, { merge: true });
    console.log('✅ User made admin successfully');
    return true;
  } catch (error) {
    console.error('❌ Error making user admin:', error);
    return false;
  }
};

// List all users in the system
export const listAllUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    const users = [];
    
    snapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    console.log('📋 All users in Firestore:', users);
    return users;
  } catch (error) {
    console.error('❌ Error listing users:', error);
    return [];
  }
};

// Get user by email
export const getUserByEmail = async (email) => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    for (const doc of snapshot.docs) {
      const userData = doc.data();
      if (userData.email === email) {
        return {
          id: doc.id,
          ...userData
        };
      }
    }
    
    console.log('❌ User not found with email:', email);
    return null;
  } catch (error) {
    console.error('❌ Error finding user by email:', error);
    return null;
  }
};

// Delete a user document (use with caution)
export const deleteUserDocument = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, { deleted: true, deletedAt: new Date() }, { merge: true });
    console.log('✅ User document marked as deleted');
    return true;
  } catch (error) {
    console.error('❌ Error deleting user document:', error);
    return false;
  }
};

// Permanently delete user from both Firebase Auth and Firestore
export const permanentlyDeleteUser = async (userId) => {
  try {
    console.log('🗑️ Starting permanent deletion of user:', userId);
    
    // First, get the user document to check if it exists
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.log('⚠️ User document not found in Firestore');
      return { success: false, message: 'User document not found' };
    }
    
    const userData = userDoc.data();
    console.log('📋 User data found:', { email: userData.email, role: userData.role });
    
    // Delete from Firestore first
    await deleteDoc(userRef);
    console.log('✅ User document deleted from Firestore');
    
    // Note: Firebase Auth deletion requires the user to be signed in
    // This is a limitation of Firebase Auth - we can't delete users from admin side
    // The user will need to be deleted manually from Firebase Console
    
    console.log('⚠️ Note: User may still exist in Firebase Auth');
    console.log('💡 To completely remove the user, delete them from Firebase Console');
    
    return { 
      success: true, 
      message: 'User deleted from Firestore. Check Firebase Console for Auth deletion.',
      email: userData.email 
    };
  } catch (error) {
    console.error('❌ Error permanently deleting user:', error);
    return { success: false, message: error.message };
  }
};

// Check if a user exists in Firebase Auth (by email)
export const checkUserExistsInAuth = async (email) => {
  try {
    // This is a workaround since we can't directly query Firebase Auth
    // We'll try to create a user with the same email and see if it fails
    const { createUserWithEmailAndPassword } = await import('firebase/auth');
    
    // Generate a temporary password
    const tempPassword = 'TempPassword123!';
    
    try {
      // Try to create a user with this email
      const userCredential = await createUserWithEmailAndPassword(auth, email, tempPassword);
      
      // If successful, immediately delete the test user
      await deleteUser(userCredential.user);
      
      console.log('✅ Email is available for use');
      return { exists: false, available: true };
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        console.log('❌ Email is already in use in Firebase Auth');
        return { exists: true, available: false };
      } else {
        console.log('⚠️ Unexpected error checking email:', error.code);
        return { exists: false, available: true };
      }
    }
  } catch (error) {
    console.error('❌ Error checking user existence:', error);
    return { exists: false, available: false, error: error.message };
  }
};

// Find orphaned user documents (exist in Firestore but not in Firebase Auth)
export const findOrphanedUsers = async () => {
  try {
    console.log('🔍 Searching for orphaned user documents...');
    
    // Get all users from Firestore
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    const firestoreUsers = [];
    
    snapshot.forEach((doc) => {
      firestoreUsers.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    console.log(`📋 Found ${firestoreUsers.length} users in Firestore`);
    
    const orphanedUsers = [];
    
    // Check each user against Firebase Auth
    for (const user of firestoreUsers) {
      const authStatus = await checkUserExistsInAuth(user.email);
      
      if (!authStatus.exists) {
        orphanedUsers.push({
          ...user,
          orphanedReason: 'No Firebase Auth account'
        });
      }
    }
    
    console.log(`⚠️ Found ${orphanedUsers.length} orphaned users`);
    return {
      success: true,
      orphanedUsers,
      totalUsers: firestoreUsers.length
    };
  } catch (error) {
    console.error('❌ Error finding orphaned users:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Clean up orphaned user documents
export const cleanupOrphanedUsers = async (userIds) => {
  try {
    console.log(`🧹 Cleaning up ${userIds.length} orphaned user documents...`);
    
    const results = [];
    
    for (const userId of userIds) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        results.push({
          userId,
          success: true,
          message: 'Document deleted successfully'
        });
        console.log(`✅ Deleted orphaned user: ${userId}`);
      } catch (error) {
        results.push({
          userId,
          success: false,
          error: error.message
        });
        console.error(`❌ Failed to delete user ${userId}:`, error);
      }
    }
    
    return {
      success: true,
      results,
      totalProcessed: userIds.length
    };
  } catch (error) {
    console.error('❌ Error cleaning up orphaned users:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Comprehensive search for user across all collections
export const searchUserAcrossAllCollections = async (email) => {
  try {
    console.log(`🔍 Searching for user with email: ${email} across all collections...`);
    
    const results = {
      email: email,
      foundIn: [],
      totalResults: 0
    };

    // List of collections to search
    const collectionsToSearch = [
      'users',
      'emailChangeRequests', 
      'userPurchases',
      'gameLaunches',
      'revenueSplits',
      'supportTickets',
      'userSessions',
      'abuseTracking',
      'gameSessions',
      'userSuspensions',
      'adminAlerts',
      'venues',
      'mfaVerifications',
      'badgeAuditLog',
      'paymentTransactions'
    ];

    for (const collectionName of collectionsToSearch) {
      try {
        console.log(`🔍 Searching in collection: ${collectionName}`);
        const collectionRef = collection(db, collectionName);
        const snapshot = await getDocs(collectionRef);
        
        const matches = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          
          // Check various email fields
          if (data.email === email || 
              data.userEmail === email || 
              data.captainEmail === email ||
              data.venueEmail === email ||
              data.creatorEmail === email) {
            matches.push({
              id: doc.id,
              collection: collectionName,
              data: data
            });
          }
        });
        
        if (matches.length > 0) {
          results.foundIn.push({
            collection: collectionName,
            matches: matches
          });
          results.totalResults += matches.length;
          console.log(`✅ Found ${matches.length} matches in ${collectionName}`);
        }
      } catch (error) {
        console.log(`⚠️ Error searching ${collectionName}:`, error.message);
      }
    }

    console.log(`📊 Search complete: Found ${results.totalResults} total matches across ${results.foundIn.length} collections`);
    return {
      success: true,
      results: results
    };
  } catch (error) {
    console.error('❌ Error searching across collections:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Check if email exists in Firebase Auth (safer method)
export const checkEmailInFirebaseAuth = async (email) => {
  try {
    console.log(`🔍 Checking if email exists in Firebase Auth: ${email}`);
    
    // Get current user to check if we're already authenticated
    const currentUser = auth.currentUser;
    
    if (currentUser && currentUser.email === email) {
      console.log('✅ Current user has this email');
      return { 
        exists: true, 
        available: false,
        message: 'This is your current email address',
        isCurrentUser: true
      };
    }
    
    // For safety, we'll just return a message asking to check manually
    // since creating test users can cause authentication issues
    return { 
      exists: null, 
      available: null,
      message: 'Please check Firebase Console → Authentication → Users manually',
      requiresManualCheck: true
    };
  } catch (error) {
    console.error('❌ Error checking Firebase Auth:', error);
    return { 
      exists: null, 
      available: null, 
      error: error.message,
      message: 'Failed to check Firebase Auth - check manually'
    };
  }
};

// Simple check for user in Firestore only
export const checkUserInFirestore = async (email) => {
  try {
    console.log(`🔍 Checking if user exists in Firestore: ${email}`);
    
    // Only check the users collection - this is safe and doesn't require special permissions
    const userInFirestore = await getUserByEmail(email);
    
    if (userInFirestore) {
      return {
        success: true,
        found: true,
        message: 'User found in Firestore users collection',
        userData: userInFirestore
      };
    } else {
      return {
        success: true,
        found: false,
        message: 'User not found in Firestore users collection'
      };
    }
  } catch (error) {
    console.error('❌ Error checking user in Firestore:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Example usage:
// makeUserAdmin('your-email@example.com'); 